import React from 'react';
import { MessageSquare, ChevronLeft, ChevronRight, Image, Video, Mic, FileText, ExternalLink, LayoutGrid } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const MessageResults = ({
    messages,
    totalCount,
    startIndex,
    endIndex,
    page,
    pageSize,
    onPageChange,
    headerOnly = false,
    contentOnly = false,
    title = 'Messages',
    emptyTitle = 'No messages found',
    emptyHint = 'Try adjusting your filters',
    rowSize = 'compact' // Key of MESSAGE_ROW_SIZES
}) => {
    const totalPages = Math.ceil(totalCount / pageSize);

    // Header Component
    const Header = () => (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
                <div className="flex items-center justify-center h-6 w-6 rounded-md bg-violet-100 dark:bg-violet-900/30">
                    <MessageSquare className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
                </div>
                <span className="font-semibold text-sm">{title}</span>
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-mono border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/50">
                    {totalCount}
                </Badge>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground mr-1">
                        {startIndex + 1}-{endIndex}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-violet-100 dark:hover:bg-violet-900/30"
                        onClick={(e) => onPageChange(e, page - 1)}
                        disabled={page === 1}
                    >
                        <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-[10px] font-medium min-w-[40px] text-center tabular-nums">
                        {page}/{totalPages}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-violet-100 dark:hover:bg-violet-900/30"
                        onClick={(e) => onPageChange(e, page + 1)}
                        disabled={page === totalPages}
                    >
                        <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                </div>
            )}

            {totalPages <= 1 && totalCount > 0 && (
                <span className="text-[10px] text-muted-foreground font-mono">
                    {startIndex + 1}-{endIndex} of {totalCount}
                </span>
            )}
        </div>
    );

    // Content Component
    const Content = () => (
        <>
            {messages.length > 0 ? (
                <div className="divide-y divide-border/50">
                    {messages.map((message) => (
                        <CompactMessageRow key={`${message.source_type || 'channel'}-${message.id}`} message={message} size={rowSize} />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <MessageSquare className="h-10 w-10 mb-2 opacity-20" />
                    <p className="text-xs font-medium">{emptyTitle}</p>
                    <p className="text-[10px] mt-0.5 opacity-60">{emptyHint}</p>
                </div>
            )}
        </>
    );

    if (headerOnly) {
        return <Header />;
    }

    if (contentOnly) {
        return <Content />;
    }

    // Full render (for backwards compatibility)
    return (
        <div className="w-full">
            <Header />
            <Content />
        </div>
    );
};

// Row styles per size: compact (search results) and comfortable (bookmark view)
const MESSAGE_ROW_SIZES = {
    compact: {
        row: "gap-2 px-2.5 py-2",
        avatar: "h-6 w-6",
        initials: "text-[8px]",
        author: "text-xs",
        time: "text-[9px]",
        text: "text-xs",
        badge: "h-4 px-1 text-[8px]",
        badgeIcon: "h-2.5 w-2.5",
    },
    comfortable: {
        row: "gap-3 px-3 py-3",
        avatar: "h-8 w-8",
        initials: "text-[10px]",
        author: "text-sm",
        time: "text-[11px]",
        text: "text-sm",
        badge: "h-5 px-1.5 text-[10px]",
        badgeIcon: "h-3 w-3",
    },
};

// Compact message row component for search results
const CompactMessageRow = ({ message, size = 'compact' }) => {
    const sizes = MESSAGE_ROW_SIZES[size];
    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    const getMediaIcon = (mediaType) => {
        switch (mediaType) {
            case 'image':
                return <Image className="h-3 w-3 text-blue-500" />;
            case 'video':
                return <Video className="h-3 w-3 text-red-500" />;
            case 'audio':
                return <Mic className="h-3 w-3 text-green-500" />;
            case 'other':
                return <FileText className="h-3 w-3 text-gray-500" />;
            default:
                return null;
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const truncateMessage = (text, maxLength = 100) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength).trim() + '...';
    };

    const handleClick = () => {
        const isCanvasComment = message.source_type === 'canvas';

        if (isCanvasComment) {
            // Open canvas item in focus mode with the comment highlighted in a new tab
            // message.channel is actually the item ID for canvas comments
            window.open(`/?itemId=${message.channel}&focusMode=true&commentId=${message.id}`, '_blank');
        } else {
            // Open channel with the message highlighted in a new tab
            if (message.channel) {
                window.open(`/channels/${message.channel}?messageId=${message.id}`, '_blank');
            }
        }
    };

    const author = message.author;
    const isCanvasComment = message.source_type === 'canvas';
    const displayName = isCanvasComment
        ? `Canvas: ${message.channel_name || 'Item'}`
        : (message.channel_name || `Channel #${message.channel}`);

    return (
        <div
            onClick={handleClick}
            data-result-row
            className={cn(
                "group flex items-start cursor-pointer",
                sizes.row,
                "hover:bg-violet-50/50 dark:hover:bg-violet-950/20 transition-colors duration-150"
            )}
        >
            {/* Author avatar */}
            <Avatar className={cn("shrink-0", sizes.avatar)}>
                {author?.picture && <AvatarImage src={author.picture} />}
                <AvatarFallback className={cn(sizes.initials, "bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300")}>
                    {author ? getInitials(author.first_name, author.last_name) : '?'}
                </AvatarFallback>
            </Avatar>

            {/* Message content */}
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                {/* Author name and timestamp */}
                <div className="flex items-center gap-1.5">
                    <span className={cn("font-medium text-foreground truncate", sizes.author)}>
                        {author ? `${author.first_name} ${author.last_name}` : 'Unknown'}
                    </span>
                    <span className={cn("text-muted-foreground shrink-0", sizes.time)}>
                        {formatTimestamp(message.created_at)}
                    </span>
                </div>

                {/* Message text or media indicator */}
                <div className="flex items-center gap-1.5">
                    {message.media_type && (
                        <span className="shrink-0">
                            {getMediaIcon(message.media_type)}
                        </span>
                    )}
                    <span className={cn("text-muted-foreground truncate leading-tight", sizes.text)}>
                        {message.message ? truncateMessage(message.message) : (
                            message.media_type ? `[${message.media_type} attachment]` : '[No content]'
                        )}
                    </span>
                </div>

                {/* Channel/Canvas indicator */}
                <div className="flex items-center gap-1">
                    <Badge
                        variant="outline"
                        className={cn(
                            "font-normal",
                            sizes.badge,
                            isCanvasComment
                                ? "border-amber-300/50 text-amber-700 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-950/30"
                                : "border-muted-foreground/20 text-muted-foreground bg-muted/50"
                        )}
                    >
                        {isCanvasComment ? (
                            <span className="flex items-center gap-0.5">
                                <LayoutGrid className={sizes.badgeIcon} />
                                {displayName}
                            </span>
                        ) : (
                            <span># {displayName}</span>
                        )}
                    </Badge>
                </div>
            </div>

            {/* Open indicator */}
            <ExternalLink className="h-3 w-3 text-muted-foreground/0 group-hover:text-violet-500/70 transition-colors shrink-0 mt-1" />
        </div>
    );
};

export default MessageResults;

